/**
 * AutoMazen Collection Cards Component
 * Handles interactive functionality for collection card layouts
 */

class AutoMazenCollectionCards {
  constructor(element) {
    this.element = element;
    this.sectionId = element.dataset.sectionId;
    this.cardsDesktop = parseInt(element.dataset.cardsDesktop) || 3;
    this.cardsMobile = parseInt(element.dataset.cardsMobile) || 1;
    this.animations = element.dataset.animations === 'true';
    this.style = element.dataset.style || 'modern';
    
    this.cards = this.element.querySelectorAll('.collection-card');
    this.grid = this.element.querySelector('.collection-grid');
    this.gridRows = this.element.querySelectorAll('.grid-row');
    
    this.initialized = false;
    this.resizeTimeout = null;
    
    this.init();
  }
  
  init() {
    if (this.initialized) return;
    
    try {
      this.setupGrid();
      this.setupCards();
      this.alignCardDescriptions();
      this.setupIntersectionObserver();
      this.setupResizeHandler();
      this.setupAccessibility();
      
      this.initialized = true;
      
      // Dispatch custom event for theme integrations
      this.element.dispatchEvent(new CustomEvent('automazen:collection-cards:initialized', {
        detail: { instance: this }
      }));
      
    } catch (error) {
      console.error('AutoMazen Collection Cards initialization error:', error);
    }
  }
  
  setupGrid() {
    if (!this.grid) return;
    
    // Set CSS custom properties for responsive grid
    this.updateGridColumns();
    
    // Add loading state
    this.grid.classList.add('loading');
    
    // Remove loading state after images load
    this.waitForImages().then(() => {
      this.grid.classList.remove('loading');
      this.grid.classList.add('loaded');
    });
  }
  
  setupCards() {
    this.cards.forEach((card, index) => {
      // Add index for potential staggered animations
      card.dataset.index = index;
      
      // Setup card interactions
      this.setupCardInteractions(card);
      
      // Setup image lazy loading enhancements
      this.setupCardImage(card);
    });
  }
  
  setupCardInteractions(card) {
    const button = card.querySelector('.card-button');
    
    if (this.animations) {
      // Add hover enter/leave handlers for enhanced animations
      card.addEventListener('mouseenter', () => {
        card.classList.add('is-hovered');
      });
      
      card.addEventListener('mouseleave', () => {
        card.classList.remove('is-hovered');
      });
    }
    
    if (button) {
      // Enhanced button interactions
      button.addEventListener('focus', () => {
        card.classList.add('is-focused');
      });
      
      button.addEventListener('blur', () => {
        card.classList.remove('is-focused');
      });
      
      // Click analytics (if needed)
      button.addEventListener('click', (e) => {
        this.trackCardClick(card, button.href);
      });
    }
  }
  
  setupCardImage(card) {
    const image = card.querySelector('.card-image img');
    if (!image) return;
    
    // Enhanced lazy loading with intersection observer
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.classList.add('is-loading');
            
            img.onload = () => {
              img.classList.remove('is-loading');
              img.classList.add('is-loaded');
            };
            
            img.onerror = () => {
              img.classList.remove('is-loading');
              img.classList.add('is-error');
            };
            
            imageObserver.unobserve(img);
          }
        });
      }, {
        rootMargin: '50px'
      });
      
      imageObserver.observe(image);
    }
  }
  
  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window) || !this.animations) return;
    
    // Staggered animation on scroll
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const card = entry.target;
          const index = parseInt(card.dataset.index) || 0;
          
          setTimeout(() => {
            card.classList.add('animate-in');
          }, index * 100);
          
          observer.unobserve(card);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });
    
    this.cards.forEach(card => {
      observer.observe(card);
    });
  }
  
  setupResizeHandler() {
    const handleResize = () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.updateGridColumns();
        this.recalculateLayout();
      }, 250);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Store reference for cleanup
    this.resizeHandler = handleResize;
  }
  
  setupAccessibility() {
    // Ensure proper ARIA labels and roles
    this.cards.forEach((card, index) => {
      const button = card.querySelector('.card-button');
      if (button) {
        // Enhance screen reader experience
        if (!button.getAttribute('aria-label')) {
          const title = card.querySelector('.card-title')?.textContent;
          if (title) {
            button.setAttribute('aria-label', `Explore ${title} collection`);
          }
        }
      }
      
      // Add keyboard navigation
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          const link = card.querySelector('.card-button');
          if (link) {
            e.preventDefault();
            link.click();
          }
        }
      });
    });
  }
  
  updateGridColumns() {
    // Method kept for compatibility but adapted for Flexbox row system
    if (!this.grid) return;
    
    const isMobile = window.innerWidth <= 768;
    const columns = isMobile ? this.cardsMobile : this.cardsDesktop;
    
    // Update CSS custom property for current responsive state
    this.grid.style.setProperty('--current-columns', columns);
    
    // Update grid rows reference in case DOM changed
    this.gridRows = this.element.querySelectorAll('.grid-row');
  }
  
  recalculateLayout() {
    // Force layout recalculation for any dynamic elements
    if (this.grid) {
      this.grid.style.display = 'none';
      this.grid.offsetHeight; // Trigger reflow
      this.grid.style.display = '';
    }
    
    // Re-align descriptions after layout change
    this.alignCardDescriptions();
  }
  
  alignCardDescriptions() {
    // Skip alignment entirely on mobile - let cards display naturally
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      // Reset any existing min-heights on mobile
      this.cards.forEach(card => {
        const title = card.querySelector('.card-title');
        if (title) {
          title.style.minHeight = '';
        }
      });
      return;
    }
    
    // Desktop/Tablet: Hybrid solution with row-based alignment
    const rows = Array.from(this.gridRows);
    if (rows.length === 0) return;
    
    // Reset any previous min-height values
    this.cards.forEach(card => {
      const title = card.querySelector('.card-title');
      if (title) {
        title.style.minHeight = '';
      }
    });
    
    // Wait for DOM to settle, then align titles within each row
    requestAnimationFrame(() => {
      rows.forEach(row => {
        const cardsInRow = Array.from(row.querySelectorAll('.collection-card'));
        const titlesInRow = cardsInRow.map(card => card.querySelector('.card-title')).filter(Boolean);
        
        // Skip empty rows
        if (titlesInRow.length === 0) return;
        
        // Find the tallest title in this specific row
        let maxHeight = 0;
        titlesInRow.forEach(title => {
          // Reset to get natural height
          title.style.minHeight = '';
          const height = title.offsetHeight;
          if (height > maxHeight) {
            maxHeight = height;
          }
        });
        
        // Set all titles in this row to match the tallest
        // This ensures descriptions align horizontally
        titlesInRow.forEach(title => {
          title.style.minHeight = `${maxHeight}px`;
        });
      });
    });
  }
  
  waitForImages() {
    const images = this.element.querySelectorAll('img');
    const promises = Array.from(images).map(img => {
      return new Promise((resolve) => {
        if (img.complete) {
          resolve();
        } else {
          img.onload = resolve;
          img.onerror = resolve;
        }
      });
    });
    
    return Promise.all(promises);
  }
  
  trackCardClick(card, url) {
    // Analytics tracking - customize as needed
    const title = card.querySelector('.card-title')?.textContent;
    
    // Google Analytics 4 example
    if (typeof gtag !== 'undefined') {
      gtag('event', 'collection_card_click', {
        card_title: title,
        card_url: url,
        section_id: this.sectionId
      });
    }
    
    // Custom event for other analytics
    document.dispatchEvent(new CustomEvent('automazen:card-click', {
      detail: {
        title,
        url,
        sectionId: this.sectionId,
        element: card
      }
    }));
  }
  
  // Public methods for external control
  refreshLayout() {
    this.updateGridColumns();
    this.recalculateLayout();
  }
  
  enableAnimations() {
    this.animations = true;
    this.element.dataset.animations = 'true';
  }
  
  disableAnimations() {
    this.animations = false;
    this.element.dataset.animations = 'false';
  }
  
  destroy() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    
    this.initialized = false;
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const collectionCardsSections = document.querySelectorAll('.automazen-collection-cards');
  
  collectionCardsSections.forEach(section => {
    new AutoMazenCollectionCards(section);
  });
});

// Shopify theme editor support
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.automazen-collection-cards');
    if (section) {
      new AutoMazenCollectionCards(section);
    }
  });
  
  document.addEventListener('shopify:section:reorder', (event) => {
    const section = event.target.querySelector('.automazen-collection-cards');
    if (section && section.automazenInstance) {
      section.automazenInstance.refreshLayout();
    }
  });
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = AutoMazenCollectionCards;
}

// Global namespace
window.AutoMazenCollectionCards = AutoMazenCollectionCards;
